import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { useSEO, SchemaOrg } from '@/components/base/SEO';
import { mangaList, articles } from '@/mocks/manga';

const SITE_URL = 'https://beilemanga.com';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const manga = useMemo(
    () => mangaList.find((m) => m.id === Number(id)),
    [id]
  );

  const relatedArticles = useMemo(
    () => articles.filter((a) => a.mangaId === Number(id)),
    [id]
  );

  const canonicalUrl = manga ? `${SITE_URL}/article/${id}` : SITE_URL;
  const pageTitle = manga
    ? `${manga.title} - 贝勒漫画 | ${manga.categoryName}漫画在线阅读`
    : '漫画详情 - 贝勒漫画';

  useSEO({
    title: pageTitle,
    description: manga ? `${manga.title}：${manga.description.substring(0, 120)}` : '贝勒漫画网漫画详情页',
    keywords: manga ? `${manga.title}, 贝勒漫画, ${manga.categoryName}, ${manga.tags.join(', ')}, 漫画在线阅读` : '贝勒漫画, 漫画详情',
    canonicalUrl,
    ogType: 'article',
    ogImage: manga?.cover,
  });

  if (!manga) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900">漫画未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 transition-colors duration-200 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const formatViews = (v: number) => {
    if (v >= 10000) return (v / 10000).toFixed(0) + '万';
    return v.toString();
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: manga.categoryName, item: `${SITE_URL}/category/${manga.category}` },
      { '@type': 'ListItem', position: 3, name: manga.title, item: canonicalUrl },
    ],
  };

  const relatedManga = mangaList.filter((m) => m.category === manga.category && m.id !== manga.id).slice(0, 4);

  return (
    <>
      <SchemaOrg data={breadcrumbSchema} />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: manga.categoryName, path: `/category/${manga.category}` },
            { label: manga.title },
          ]}
        />

        <article className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-10">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Cover Sidebar */}
            <aside className="lg:w-72 shrink-0">
              <div className="sticky top-24">
                <div className="rounded-lg overflow-hidden bg-background-50 border border-background-200/70">
                  <img
                    src={manga.cover}
                    alt={manga.title}
                    className="w-full aspect-[3/4] object-cover object-top"
                    title={`${manga.title} - 贝勒漫画`}
                  />
                </div>
                <div className="mt-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded text-xs font-medium whitespace-nowrap ${
                      manga.status === '连载中' ? 'bg-primary-500 text-background-50' : 'bg-secondary-500 text-background-50'
                    }`}>
                      {manga.status}
                    </span>
                    <span className="text-xs text-foreground-600">更新至第{manga.chapters}话</span>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                {manga.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-600 mb-4">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                  {manga.author}
                </span>
                <span>|</span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                  {formatViews(manga.views)}次阅读
                </span>
                <span>|</span>
                <span className="flex items-center gap-1">
                  <i className="ri-star-fill w-4 h-4 flex items-center justify-center text-accent-500"></i>
                  {manga.rating}分
                </span>
                <span>|</span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                  {manga.updateTime}更新
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {manga.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>

              {/* Description */}
              <div className="bg-background-100 rounded-lg p-5 mb-8">
                <h3 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h3>
                <p className="text-sm text-foreground-700 leading-relaxed">{manga.description}</p>
              </div>

              {/* Chapter List / Articles */}
              {relatedArticles.length > 0 && (
                <section className="mb-8">
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                    最新章节
                  </h2>
                  <div className="space-y-3">
                    {relatedArticles.map((article) => (
                      <div
                        key={article.id}
                        className="bg-background-50 border border-background-200/70 rounded-lg p-4 hover:border-primary-300 transition-all duration-200"
                      >
                        <h3 className="text-sm font-semibold text-foreground-900 mb-2">
                          {article.title}
                        </h3>
                        <p className="text-xs text-foreground-600 mb-3">
                          {article.publishTime} · {formatViews(article.views)}阅读 · {article.likes}赞
                        </p>
                        <div
                          className="text-sm text-foreground-700 leading-relaxed prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: article.content }}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Related Manga */}
              {relatedManga.length > 0 && (
                <section>
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                    同类推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                    {relatedManga.map((rm) => (
                      <Link
                        key={rm.id}
                        to={`/article/${rm.id}`}
                        className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                      >
                        <div className="aspect-[3/4] overflow-hidden">
                          <img
                            src={rm.cover}
                            alt={rm.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-2">
                          <p className="text-xs font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                            {rm.title}
                          </p>
                          <p className="text-[10px] text-foreground-500 mt-0.5">{rm.categoryName} · {rm.rating}分</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}